/**
 * 중앙 에러 레지스트리 + 단일 응답 봉투.
 *
 * ─── 왜 한 파일에 평평한 표로 두는가 ───
 * allpawhub 의 에러 레지스트리에 이렇게 적혀 있다: **"(status, code) 쌍은 API 계약이므로
 * 바뀌면 안 된다."** 클라이언트가 code 로 분기하고 status 로 재시도를 정한다.
 * 흩어져 있으면 같은 상황에 다른 code 가 붙고, 그때부터 클라이언트가 문자열 매칭을 시작한다.
 * 구 stationon-erp 는 컨트롤러 27,962줄에 JSON 조립이 흩어져 있었다.
 *
 * ─── 문구 규약은 BI 문서에서 온다 (§05 Tone of Voice) ───
 * BI 가 든 실제 예시:
 *     ✗ "승인할 수 없습니다"              ✔ "근무 기록 2건을 먼저 확인하면 승인할 수 있습니다."
 *     ✗ "오류가 발생했습니다!"            ✔ "저장되지 않았습니다. 입력 내용은 유지되어 있습니다."
 *     ✗ "해당 오브젝트가 유효하지 않습니다"  ✔ "선택한 근무지가 삭제되어 다시 지정해야 합니다."
 * 즉 오류 문구는 **무엇이 달라졌고 다음에 무엇을 해야 하는지**를 말한다.
 * 그래서 모든 항목에 `nextActionKey` 를 요구하고, 없으면 `noNextAction` 에 이유를 적게 한다 —
 * 규약을 문서가 아니라 타입과 테스트가 강제한다. 문서로만 두면 반드시 어긋난다.
 *
 * 문구 자체는 여기 없다. i18n 키만 있다. 코어는 렌더된 한국어 문자열을 모른다 —
 * 명세서 PDF·모바일·웹이 각자의 로케일로 렌더한다.
 */

/** 성공/실패 봉투. 모든 응답이 이 둘 중 하나다. */
export type Envelope<T> =
  | { success: true; data: T }
  | { success: false; error: { code: string; message: string; details?: unknown } };

export interface ErrorSpec {
  /** 클라이언트가 분기하는 안정 식별자. 한 번 정해지면 바꾸지 않는다. */
  readonly code: string;
  readonly status: number;
  /** i18n 키. 렌더된 문자열이 아니다. */
  readonly messageKey: string;
  /**
   * BI §05 — 사용자가 **다음에 할 일**. 이게 없으면 "실패했습니다"로 끝나는 문구가 된다.
   * 정말 다음 행동이 없는 경우에만 생략하고 `noNextAction` 에 이유를 적는다.
   */
  readonly nextActionKey?: string;
  readonly noNextAction?: string;
  /** true 면 클라이언트가 그대로 재시도해도 된다. */
  readonly retryable: boolean;
}

/**
 * 항목을 ErrorSpec 으로 **넓혀서** 담는다. `<const T extends ErrorSpec>` 로 좁히면
 * 각 항목이 자기 리터럴 타입이 되어 `Object.values(ERRORS)` 가 유니온이 되고,
 * 그 유니온에서는 일부 멤버에 없는 선택 필드(nextActionKey/noNextAction)를 못 읽는다.
 * code 의 리터럴 타입은 객체의 **키**에서 오므로(ErrorCode) 여기서 좁힐 이유가 없다.
 */
function spec(s: ErrorSpec): ErrorSpec {
  return s;
}

/**
 * 전 제품의 에러 목록. **새 에러는 여기에 추가한다.**
 * 여기에 없는 것을 던지면 500 + 내부 로그이고, 내부 정보는 절대 노출되지 않는다.
 */
export const ERRORS = {
  'auth.unauthenticated': spec({
    code: 'auth.unauthenticated',
    status: 401,
    messageKey: 'core.error.auth.unauthenticated',
    nextActionKey: 'core.error.auth.unauthenticated.next', // "다시 로그인하면 이어서 진행할 수 있습니다."
    retryable: false,
  }),
  'auth.forbidden': spec({
    code: 'auth.forbidden',
    status: 403,
    messageKey: 'core.error.auth.forbidden',
    // BI §05: "권한이 없습니다" 로 끝내지 않는다. 누구에게 요청하면 되는지 말한다.
    nextActionKey: 'core.error.auth.forbidden.next',
    retryable: false,
  }),
  /*
   * ── 비밀번호 변경의 세 거절 ─────────────────────────────────────────────────
   * 셋 다 **422 다. 401 이 아니다** — 401 은 `classifyRpcError` 가 `unauthenticated` 로
   * 접고 `<SessionWatcher>` 가 그것을 "세션이 끝났다"로 실행한다(0008 §401 의 소유자).
   * 현재 비밀번호 오타에 그 정책이 돌면 **비밀번호를 바꾸려다 로그아웃된다.**
   *
   * 그리고 셋을 하나로 접지 않은 이유가 `nextActionKey` 세 줄이다: 다시 입력해라 ·
   * 더 길게 써라 · 다른 것을 골라라. BI §05 가 금지하는 것이 정확히 "실패했습니다"로
   * 끝나는 문구이고, 이 셋을 합치면 그 문장 하나만 남는다.
   *
   * ★ 로그인의 "이유를 나누지 않는다"(0023)와 **반대 방향인 것이 맞다.** 거기서 이유를
   * 숨기는 것은 부르는 사람이 계정 주인이 아닐 수 있어서다. 여기서는 부르는 사람이
   * 이미 그 계정으로 인증돼 있다 — 숨겨서 지킬 비밀이 없고, 숨기면 오타를 못 고친다.
   */
  'auth.currentPasswordMismatch': spec({
    code: 'auth.currentPasswordMismatch',
    status: 422,
    messageKey: 'core.error.auth.currentPasswordMismatch',
    nextActionKey: 'core.error.auth.currentPasswordMismatch.next',
    retryable: false,
  }),
  'auth.passwordTooShort': spec({
    code: 'auth.passwordTooShort',
    status: 422,
    // 문장에 `{min}` 이 있다. 렌더할 때 params 를 준다 — 서버는 `AppError` 의 params 로,
    // 화면은 `t(key, { min: MIN_PASSWORD_LENGTH })` 로. 숫자를 문장에 박으면 그것이 사본이다.
    messageKey: 'core.error.auth.passwordTooShort',
    nextActionKey: 'core.error.auth.passwordTooShort.next',
    retryable: false,
  }),
  'auth.passwordUnchanged': spec({
    code: 'auth.passwordUnchanged',
    status: 422,
    messageKey: 'core.error.auth.passwordUnchanged',
    nextActionKey: 'core.error.auth.passwordUnchanged.next',
    retryable: false,
  }),
  /**
   * 재설정 링크가 유효하지 않다 — **없음 · 만료 · 이미 씀 셋을 하나로 접은 것이다.**
   *
   * 셋 다 다음 행동이 "다시 요청하세요" 하나이고, 갈라 주면 그 응답이 "이 토큰이
   * 존재하긴 했다"를 알려 준다. 위 셋(비밀번호 변경)이 이유를 나눈 것과 **반대 방향인
   * 것이 맞다**: 거기서는 부르는 사람이 이미 인증돼 있고, 여기서는 링크 하나가 신원의
   * 전부다. 서버는 셋을 구분한다 — 로그에만 남는다(docs/decisions/0036).
   *
   * 401 이 아닌 이유는 0024 와 같다: `classifyRpcError` 가 401 을 세션 종료로 실행하는데,
   * 그러면 만료된 링크 하나가 **다른 탭의 멀쩡한 세션을 끊는다.**
   */
  'auth.resetTokenInvalid': spec({
    code: 'auth.resetTokenInvalid',
    status: 422,
    messageKey: 'core.error.auth.resetTokenInvalid',
    nextActionKey: 'core.error.auth.resetTokenInvalid.next',
    retryable: false,
  }),
  /**
   * 한 이메일에 붙은 회사가 상한을 넘었다 (0056).
   *
   * ★ **거절을 `auth.unauthenticated` 로 접지 않는 유일한 로그인 실패다.** 나머지는
   * 계정 열거를 막으려고 한 문장으로 접는데, 여기는 반대다: 이 상태에 도달하려면
   * **우리가** 같은 이메일로 상한 넘게 계정을 만들어 준 것이고, 공격자가 만들 수 있는
   * 데이터가 아니다. 접어 버리면 그 사람은 "비밀번호가 맞는데 계속 틀리다고 한다"가
   * 되고, 고객지원이 붙잡을 단서가 서버 로그 한 줄뿐이다.
   *
   * 401 이 아닌 이유는 0024·0036 과 같다 — 401 은 클라이언트에서 세션 종료로 실행된다.
   */
  'auth.tooManyCompanies': spec({
    code: 'auth.tooManyCompanies',
    status: 422,
    messageKey: 'core.error.auth.tooManyCompanies',
    nextActionKey: 'core.error.auth.tooManyCompanies.next',
    retryable: false,
  }),
  /**
   * ══ 셀프 가입 · 소셜 · 초대 (0225) ══════════════════════════════════════════
   *
   * 전부 422 다 — 401 은 클라이언트에서 세션 종료로 실행되고(0024·0036), 이 문들을 여는 사람은
   * 대개 로그인하지 않았거나 **다른 탭에서 멀쩡히 로그인해 있다.** 만료된 링크 하나가 그
   * 세션을 끊으면 안 된다.
   *
   * 링크 토큰의 무효(없음·만료·이미 씀)는 재설정과 같은 이유로 **하나로 접는다**.
   */
  'auth.signupTokenInvalid': spec({
    code: 'auth.signupTokenInvalid',
    status: 422,
    messageKey: 'core.error.auth.signupTokenInvalid',
    nextActionKey: 'core.error.auth.signupTokenInvalid.next',
    retryable: false,
  }),
  /**
   * 가입 폼의 휴대폰이 휴대폰 번호가 아니다(0234). 이메일 가입은 필수, 소셜 가입은 적었을 때만 본다.
   * 링크 토큰은 **아직 소진되지 않았다** — 고쳐서 다시 누르면 된다.
   */
  'auth.phoneInvalid': spec({
    code: 'auth.phoneInvalid',
    status: 422,
    messageKey: 'core.error.auth.phoneInvalid',
    nextActionKey: 'core.error.auth.phoneInvalid.next',
    retryable: false,
  }),
  'auth.inviteTokenInvalid': spec({
    code: 'auth.inviteTokenInvalid',
    status: 422,
    messageKey: 'core.error.auth.inviteTokenInvalid',
    nextActionKey: 'core.error.auth.inviteTokenInvalid.next',
    retryable: false,
  }),
  /** 제공자와의 왕복이 실패했다(state 불일치 · 코드 교환 실패 · 동의 취소). 처음부터 다시가 답이다. */
  'auth.socialFailed': spec({
    code: 'auth.socialFailed',
    status: 422,
    messageKey: 'core.error.auth.socialFailed',
    nextActionKey: 'core.error.auth.socialFailed.next',
    retryable: false,
  }),
  /** 제공자가 확인된 이메일을 안 줬다. 이메일이 로그인 아이디이자 재설정 주소라 그것 없이는 못 만든다. */
  'auth.socialEmailMissing': spec({
    code: 'auth.socialEmailMissing',
    status: 422,
    messageKey: 'core.error.auth.socialEmailMissing',
    nextActionKey: 'core.error.auth.socialEmailMissing.next',
    retryable: false,
  }),
  /** 이 서버에 그 제공자가 켜져 있지 않다(키가 없다). 버튼을 안 그리므로 보통은 손으로 만든 요청이다. */
  'auth.socialUnavailable': spec({
    code: 'auth.socialUnavailable',
    status: 422,
    messageKey: 'core.error.auth.socialUnavailable',
    nextActionKey: 'core.error.auth.socialUnavailable.next',
    retryable: false,
  }),
  /**
   * 앱에서 시작한 소셜 로그인인데 연결된 계정이 없다. 0228 부터는 **가입 갈래를 모르는 옛 iOS 빌드의 Apple
   * 로그인**에만 온다(새 빌드는 `allowSignup` 을 보내 가입 티켓을 받는다).
   */
  'auth.socialNoAccount': spec({
    code: 'auth.socialNoAccount',
    status: 422,
    messageKey: 'core.error.auth.socialNoAccount',
    nextActionKey: 'core.error.auth.socialNoAccount.next',
    retryable: false,
  }),
  /** 그 외부 계정은 이 회사의 다른 사람에게 이미 연결돼 있다. */
  'auth.socialTaken': spec({
    code: 'auth.socialTaken',
    status: 409,
    messageKey: 'core.error.auth.socialTaken',
    nextActionKey: 'core.error.auth.socialTaken.next',
    retryable: false,
  }),
  /** 비밀번호가 없는 사람의 마지막 소셜 연결을 끊으려 했다 — 끊으면 들어올 문이 없다. */
  'auth.lastSignInMethod': spec({
    code: 'auth.lastSignInMethod',
    status: 409,
    messageKey: 'core.error.auth.lastSignInMethod',
    nextActionKey: 'core.error.auth.lastSignInMethod.next',
    retryable: false,
  }),
  /**
   * 계정을 지우면 회사를 설정할 사람이 없어진다 — 로그인할 수 있는 다른 사람은 있는데 오너 역할을 가진 사람이
   * 이 사람뿐이다(0229). 오너를 넘기는 것이 다음 행동이다. 혼자인 회사는 여기 안 온다(그 삭제는 회사를 닫는다).
   */
  'auth.accountLastOwner': spec({
    code: 'auth.accountLastOwner',
    status: 409,
    messageKey: 'core.error.auth.accountLastOwner',
    nextActionKey: 'core.error.auth.accountLastOwner.next',
    retryable: false,
  }),
  /** 초대할 수 없다 — 이메일이 없다. 명부에서 이메일을 넣는 것이 다음 행동이다. */
  'people.inviteNoEmail': spec({
    code: 'people.inviteNoEmail',
    status: 422,
    messageKey: 'core.error.people.inviteNoEmail',
    nextActionKey: 'core.error.people.inviteNoEmail.next',
    retryable: false,
  }),
  /** 초대할 필요가 없다 — 이미 로그인할 수 있다. 비밀번호를 잊었으면 재설정이 그 문이다. */
  'people.inviteAlreadyActive': spec({
    code: 'people.inviteAlreadyActive',
    status: 409,
    messageKey: 'core.error.people.inviteAlreadyActive',
    nextActionKey: 'core.error.people.inviteAlreadyActive.next',
    retryable: false,
  }),
  'tenant.missingContext': spec({
    code: 'tenant.missingContext',
    status: 500,
    messageKey: 'core.error.tenant.missingContext',
    // 사용자가 할 수 있는 일이 없다. 이건 우리 버그다.
    noNextAction: '테넌트 컨텍스트 누락은 서버 결함이다 — 사용자 행동으로 해결되지 않는다',
    retryable: false,
  }),
  'validation.failed': spec({
    code: 'validation.failed',
    status: 422,
    messageKey: 'core.error.validation.failed',
    nextActionKey: 'core.error.validation.failed.next', // "표시된 항목을 수정하면 저장됩니다. 입력 내용은 유지되어 있습니다."
    retryable: false,
  }),
  'resource.notFound': spec({
    code: 'resource.notFound',
    status: 404,
    messageKey: 'core.error.resource.notFound',
    nextActionKey: 'core.error.resource.notFound.next',
    retryable: false,
  }),
  'resource.conflict': spec({
    code: 'resource.conflict',
    status: 409,
    messageKey: 'core.error.resource.conflict',
    nextActionKey: 'core.error.resource.conflict.next',
    retryable: false,
  }),
  /**
   * 마지막 권한 관리자를 해제하려 했다. **DB 가 판정한다** —
   * `core.assert_admin_remains()` 가 지연 제약 트리거로 막고, 이 코드는 그 23514 를
   * 사용자가 읽을 수 있는 것으로 옮기는 자리다.
   *
   * `internal.unexpected` 로 흘리면 "문제가 생겨 처리하지 못했습니다"가 뜬다 —
   * DB 는 이미 **다음 행동**까지 알고 있는데(hint: 먼저 다른 구성원에게 역할 관리 권한을
   * 부여하세요) 그것을 버리는 것이고, BI §05 가 금지하는 형태다.
   */
  'rbac.lastAdmin': spec({
    code: 'rbac.lastAdmin',
    status: 409,
    messageKey: 'core.error.rbac.lastAdmin',
    nextActionKey: 'core.error.rbac.lastAdmin.next',
    retryable: false,
  }),
  'module.notEnabled': spec({
    code: 'module.notEnabled',
    status: 404,   // 403 이 아니다. 비활성 모듈의 존재 자체를 알리지 않는다.
    messageKey: 'core.error.module.notEnabled',
    nextActionKey: 'core.error.module.notEnabled.next',
    retryable: false,
  }),
  'rateLimit.exceeded': spec({
    code: 'rateLimit.exceeded',
    status: 429,
    messageKey: 'core.error.rateLimit.exceeded',
    nextActionKey: 'core.error.rateLimit.exceeded.next',
    retryable: true,
  }),
  'upstream.unavailable': spec({
    code: 'upstream.unavailable',
    status: 503,
    messageKey: 'core.error.upstream.unavailable',
    nextActionKey: 'core.error.upstream.unavailable.next',
    retryable: true,
  }),
  'internal.unexpected': spec({
    code: 'internal.unexpected',
    status: 500,
    messageKey: 'core.error.internal.unexpected',
    noNextAction: '원인을 모르는 상태다. 사용자에게 지시할 다음 행동이 없다',
    retryable: false,
  }),
} as const;

export type ErrorCode = keyof typeof ERRORS;

/**
 * 던질 수 있는 에러. **레지스트리에 있는 code 만 만들 수 있다** — 타입이 강제한다.
 * `details` 는 클라이언트에게 나가므로 내부 정보를 넣지 않는다.
 */
export class AppError extends Error {
  readonly spec: ErrorSpec;
  readonly params: Readonly<Record<string, unknown>>;
  readonly details?: unknown;

  constructor(code: ErrorCode, opts: { params?: Record<string, unknown>; details?: unknown; cause?: unknown } = {}) {
    const spec = ERRORS[code];
    super(spec.messageKey, { cause: opts.cause });
    this.name = 'AppError';
    this.spec = spec;
    this.params = Object.freeze({ ...opts.params });
    this.details = opts.details;
  }
}

/**
 * 무엇이 던져졌든 봉투로 만든다.
 *
 * **미분류 에러는 500 + `internal.unexpected` 로만 나간다.** 원본 메시지를 노출하지 않는다 —
 * 스택·SQL·파일 경로가 그대로 새는 경로가 여기다. 원본은 로거로만 간다.
 */
export function toEnvelope(
  err: unknown,
  render: (key: string, params?: Record<string, unknown>) => string,
  log: (msg: string, fields: Record<string, unknown>) => void,
): { status: number; body: Envelope<never> } {
  if (err instanceof AppError) {
    const parts = [render(err.spec.messageKey, err.params)];
    if (err.spec.nextActionKey) parts.push(render(err.spec.nextActionKey, err.params));
    return {
      status: err.spec.status,
      body: { success: false, error: { code: err.spec.code, message: parts.join(' '), details: err.details } },
    };
  }
  const s = ERRORS['internal.unexpected'];
  log('미분류 에러', { err: err instanceof Error ? err.stack ?? err.message : String(err) });
  return { status: s.status, body: { success: false, error: { code: s.code, message: render(s.messageKey) } } };
}

export function ok<T>(data: T): Envelope<T> {
  return { success: true, data };
}
